import { groq } from 'next-sanity';
import { useEffect, useState } from 'react';
import Disclaimer from '../../../components/Disclaimer';
import Hero from '../../../components/Hero';
import Layout from '../../../components/Layout';
import Section from '../../../components/Section';
import CampaignCard from '../../../components/cards/CampaignCard';
import { accessoryTagPageQuery } from '../../../groq/pages/accessories';
import { sanityClient } from '../../../lib/sanity.server';
import {
  CampaignSingularType,
  DefaultSEOType,
  MenuType,
  SEOType,
  SettingsType,
} from '../../../types';
import { AccessoryType } from '../../../types/accessory';
import { ImageWithAltType } from '../../../types/imageWithAlt';
import fetchSanityData from '../../../utils/fetchSanityData';
import CampaignBox from '../../../components/CampaignBox';

interface PageProps {
  data: {
    menus: MenuType;
    settings: SettingsType;
    content: {
      _id: string;
      overline?: string;
      image?: ImageWithAltType;
      slug: string;
      tagName: string;
      description: string;
      seo?: SEOType;
      defaultSEO: DefaultSEOType;
    };
    accessories: AccessoryType[];
  };
}

function AccessoriesPage({ data }: PageProps) {
  const {
    menus,
    settings,
    accessories,
    content: { _id, slug, tagName, image: topImage, description, seo, defaultSEO, overline },
  } = data;

  const [campaigns, setCampaigns] = useState<
    | {
        campaignsCategory: CampaignSingularType[] | undefined;
        campaignsProduct: string[] | undefined;
        campaignsProductItem: CampaignSingularType[] | undefined;
      }
    | undefined
  >(undefined);

  useEffect(() => {
    const fetchCampaigns: () => void = async () => {
      const response = await fetchSanityData('accessoryTagPageCampaigns', { id: _id, slug });
      setCampaigns(response.campaigns);
    };

    fetchCampaigns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let disclaimerNo = 1;

  const norwegianCollator = new Intl.Collator('nb-NO', { sensitivity: 'base' });
  const campaignAccessories = accessories
    .filter((accessory) => campaigns?.campaignsProduct?.includes(accessory._id))
    .sort((a, b) => norwegianCollator.compare(a.title, b.title));
  const remainingAccessories = accessories
    .filter((accessory) => !campaigns?.campaignsProduct?.includes(accessory._id))
    .sort((a, b) => norwegianCollator.compare(a.title, b.title));

  const sortedAccessories = [...campaignAccessories, ...remainingAccessories];

  return (
    <Layout settings={settings} menus={menus} seo={seo} defaultSEO={defaultSEO}>
      <Hero
        title={tagName}
        description={description}
        image={topImage}
        overline={overline}
        background="primary"
      />

      <Section>
        <div className="flex-col gap-20">
          {campaigns?.campaignsCategory &&
            campaigns?.campaignsCategory?.length > 0 &&
            campaigns?.campaignsCategory?.map((campaign) => (
              <div className="pb-8" key={campaign._id}>
                <CampaignBox
                  key={campaign._id}
                  campaign={campaign}
                  itemNo={campaign.disclaimer ? disclaimerNo++ : 0}
                />
              </div>
            ))}

          {!!campaigns?.campaignsProductItem?.length && (
            <div>
              <h2 className="mb-5 text-3xl font-semibold lg:mb-10 lg:text-4xl">Kampanjer</h2>
              <div className="mb-24 grid">
                {campaigns.campaignsProductItem?.map((campaign) => {
                  return (
                    <CampaignCard
                      key={campaign._id}
                      overline={campaign.overline}
                      title={campaign.title}
                      description={campaign.pitch}
                      image={campaign.image}
                      href={`${`${slug}/${encodeURI(campaign?.campaignItem?.slug?.current || '/')}`}`}
                    />
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <h2 className="mb-5 text-3xl font-semibold lg:mb-10 lg:text-4xl">{`Vårt utvalg av ${tagName.toLowerCase()}`}</h2>
            <section className="grid gap-5">
              {Array.isArray(accessories) &&
                sortedAccessories.map((accessory) => {
                  const {
                    _id: accessoryId,
                    overline: accessoryOverline,
                    title: accessoryTitle,
                    description: accessoryDescription,
                    image,
                    accessoryTag,
                    slug: accessorySlug,
                  } = accessory;

                  return campaigns?.campaignsProduct?.includes(accessoryId) ? (
                    <CampaignCard
                      key={accessoryId}
                      overline={accessoryOverline}
                      description={accessoryDescription}
                      title={accessoryTitle}
                      href={`${accessoryTag}/${accessorySlug}`}
                      image={image}
                    />
                  ) : (
                    <CampaignCard
                      key={accessoryId}
                      overline={accessoryOverline}
                      description={accessoryDescription}
                      title={accessoryTitle}
                      href={`${accessoryTag}/${accessorySlug}`}
                      image={image}
                    />
                  );
                })}
            </section>
          </div>

          <div className="mx-auto my-10 max-w-3xl px-6 sm:px-10 lg:my-20">
            <Disclaimer
              text={`${
                campaigns?.campaignsCategory?.filter((item) => item.disclaimer).length
                  ? campaigns?.campaignsCategory
                      .filter((item) => item.disclaimer)
                      .map(
                        (campaign, index) =>
                          `${new Array(index + 1).fill('*').join('')}${campaign.disclaimer}`,
                      )
                      .join('\n')
                  : ''
              }`}
            />
          </div>
        </div>
      </Section>
    </Layout>
  );
}

export async function getStaticPaths() {
  const paths = await sanityClient.fetch(groq`*[_type == "accessoryTag" ].slug.current`);

  return {
    paths: paths.map((tag: string) => ({ params: { tag } })),
    fallback: 'blocking',
  };
}

export const getStaticProps = async ({ params }: { params: { tag: string } }) => {
  const { tag = '' } = params;
  const data = await sanityClient.fetch(accessoryTagPageQuery, { accessoryTag: tag });
  if (!data.content) return { notFound: true };

  return {
    props: {
      data,
    },
    revalidate: 1,
  };
};

export default AccessoriesPage;
